import { useEffect, useRef } from 'react';
import { App as CapApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { campaignFromUrl, setCampaign } from 'common/utils/partnerCampaign';
import { BiometricAuth } from '@aparajita/capacitor-biometric-auth';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'common/hooks/state';
import { RootState } from 'common/store/store';
import { linkPath, shortcutScanQueryParam, shortcutScanQueryValue } from 'common/constants';
import { queueNavigation } from 'common/utils/pushRegistration';
import { resolveInAppPath, UNIVERSAL_LINK_HOSTS } from 'common/utils/appLinks';
import { lockSession, unlockSession, selectIsLocked } from 'common/store/lockSlice';
import { isBiometricPromptInFlight, withBiometricPrompt } from 'common/utils/biometricGate';
import { parseObjectPropertiesToCamelCase } from 'common/utils/helpers';
import {
  mixpanelCustomEvent, trackEventAnon, MixpanelEventName, Dict,
} from 'common/utils/mixpanel/eventTriggers';

// sessionStorage key the paywall code reads to thread the campaign attribute
// into RevenueCat (see src/features/pricing/showPaywall.ts).
export const CAMPAIGN_TOKEN_STORAGE_KEY = '_campaign_token';

// Universal-link routing: the allowlist lives in common/utils/appLinks.ts and
// mirrors server.js's UNIVERSAL_LINK_PATHS - keep both in sync. A path off the
// allowlist is a silent no-op (the app is already open, which is fine).

// App shortcuts (Android static shortcuts / iOS UIApplicationShortcutItems,
// Tasks 2-3) both forward into this same doqit-app:// pipeline as
// `doqit-app://shortcut/add`, `.../scan` and `.../search` - i.e.
// hostname 'shortcut', pathname '/<name>'. An unrecognised name is a silent
// no-op, matching the existing non-match behaviour for other doqit-app: URLs.
const SHORTCUT_HOST = 'shortcut';
type ShortcutName = 'add' | 'scan' | 'search';
const isShortcutName = (value: string): value is ShortcutName => value === 'add' || value === 'scan' || value === 'search';

// A tapped Spotlight search result (Task 3 of the Spotlight-indexing plan)
// forwards into this same doqit-app:// pipeline as `doqit-app://document/<id>`
// - i.e. hostname 'document', pathname '/<id>' - built natively by
// AppDelegate.swift's forwardSpotlightTap from the tapped item's Spotlight
// identifier. See docs/spotlight-runbook.md.
const DOCUMENT_HOST = 'document';
// The id comes from a native OS payload (the tapped Spotlight item's
// identifier), so it is never trusted enough to navigate on unvalidated -
// digits-only, matching matchUniversalLinkPath's existing
// /^\/editDocuments\/\d+$/ idiom for the same route shape above.
const isNumericDocumentId = (value: string): boolean => /^\d+$/.test(value);

const AppLifecycleHandler = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { token, user } = useAppSelector((state: RootState) => state.auth);

  // Refs to store the token and authentication state
  const tokenRef = useRef(token);
  const isAuthenticatingRef = useRef(false);
  const lastPausedTimeRef = useRef<number | null>(null);
  // Lock state as of the last trip to the background. Read through refs so
  // the mount-only appStateChange listener below sees current values.
  const isLocked = useAppSelector(selectIsLocked);
  const isLockedRef = useRef(isLocked);
  const wasLockedBeforePauseRef = useRef(false);
  // Read fresh inside handleDeepLinkUrl (defined in a mount-only effect
  // below) the same way tokenRef lets it read a fresh token - a shortcut can
  // fire long after mount, e.g. via a warm appUrlOpen resume.
  const userRef = useRef(user);

  // Update the tokenRef whenever the token changes
  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    isLockedRef.current = isLocked;
  }, [isLocked]);

  // Deep-link handling for the event QR code (doqit-app://paywall?campaign=...).
  // Runs on every platform — on web the listeners just no-op because Capacitor's
  // App plugin doesn't fire web events of this shape. We capture the campaign
  // token on cold-start (getLaunchUrl) AND on warm resume (appUrlOpen) and stash
  // it in sessionStorage so the paywall layer can pass it to RevenueCat when
  // the user reaches the paywall through their natural flow.
  //
  // IMPORTANT: this listener intentionally does NOT navigate. The paywall is
  // gated by RC and shown at the end of onboarding / behind premium features
  // by the existing app flow — scanning the QR pre-stages the campaign signal
  // for that flow rather than triggering an unsolicited paywall popup.
  useEffect(() => {
    // Cold launch delivers the SAME url through both paths below:
    // CapApp.getLaunchUrl() reads Capacitor's retained lastURL, and the
    // appUrlOpen listener immediately replays that same retained event to
    // the newly-attached listener (retainUntilConsumed) - so a single cold
    // launch calls handleDeepLinkUrl twice with an identical url, normally
    // within milliseconds of each other. The pre-existing side effects
    // tolerated this (sessionStorage.setItem is idempotent, queueNavigation
    // is a last-write-wins single slot), but MixpanelEventName.shortcutLaunched
    // is not - it would double-count every cold-launch shortcut tap.
    //
    // Deduped here by remembering the last URL genuinely handled and its
    // timestamp, scoped to this effect's closure (i.e. this mount, not a
    // module global - a real app relaunch remounts this component with a
    // fresh closure regardless). An identical url arriving again within
    // DEDUPE_WINDOW_MS is treated as the second delivery of the SAME
    // launch and skipped entirely (not just for analytics, since any
    // future non-idempotent side effect added to this handler gets the
    // same protection for free). A short window rather than "ignore this
    // url forever" is deliberate: it must still fire twice for two
    // genuinely separate user actions with the same url (e.g. tapping the
    // same shortcut again after backgrounding and reopening the app) -
    // that always takes several seconds minimum in practice (the app has
    // to leave the foreground and the OS has to relaunch it), comfortably
    // outside this window, while the two duplicate-delivery paths for one
    // launch are always effectively simultaneous.
    const DEDUPE_WINDOW_MS = 1500;
    let lastHandledUrl: string | null = null;
    let lastHandledAt: number | null = null;

    // /learn and /learn/:slug are public routes, so they open straight away
    // (a signed-out user lands on the video). Everything else waits in the
    // auth-gated queue.
    const routeTo = (path: string) => {
      const basePath = path.split('?')[0];
      if (basePath === '/learn' || basePath.startsWith('/learn/')) navigate(path);
      else queueNavigation(path);
    };

    const handleDeepLinkUrl = (url: string | undefined | null) => {
      if (!url) return;
      const now = Date.now();
      if (lastHandledUrl === url && lastHandledAt !== null && now - lastHandledAt < DEDUPE_WINDOW_MS) {
        return;
      }
      lastHandledUrl = url;
      lastHandledAt = now;
      try {
        const parsed = new URL(url);
        // Partner and event links (?campaign=, or /partner/<slug>): remember the tag
        // so the paywall shows that partner's offering (partnerCampaign.ts).
        const campaign = campaignFromUrl(url);
        if (campaign) {
          trackEventAnon(MixpanelEventName.deepLinkOpened, { link_type: 'campaign', campaign, cold_start: !tokenRef.current });
          setCampaign(campaign, 'link');
        }

        // Universal links (https://app.doqit.io/... or the UAT host): route
        // into the app via the same auth-gated queue push taps use. Any
        // other https host, or a path off the allowlist, is left alone -
        // the OS already opened the app, so doing nothing is a fine outcome.
        if (parsed.protocol === 'https:' && UNIVERSAL_LINK_HOSTS.includes(parsed.hostname)) {
          const path = resolveInAppPath(url);
          if (path) {
            const routeLabels: Record<string, string> = {
              '/reminders': 'reminders',
              '/document/upload': 'add',
              '/dashboard': 'dashboard',
              '/searchDocument': 'search',
              '/documents': 'documents',
              '/learn': 'learn',
            };
            const basePath = path.split('?')[0];
            let route = routeLabels[basePath] ?? 'document';
            if (basePath.startsWith('/learn/')) route = 'learn';
            else if (basePath.startsWith('/offer/')) route = 'offer';
            trackEventAnon(MixpanelEventName.deepLinkOpened, { link_type: 'universal', route, cold_start: !tokenRef.current });
            routeTo(path);
          }
        }

        // Custom-scheme offer and learn links (doqit://offer/rescue,
        // doqit-app://learn/take-a-photo). Same auth-gated queue as above.
        if ((parsed.protocol === 'doqit:' || parsed.protocol === 'doqit-app:')
          && (parsed.hostname === 'offer' || parsed.hostname === 'learn')) {
          const path = resolveInAppPath(url);
          if (path) {
            trackEventAnon(MixpanelEventName.deepLinkOpened, { link_type: 'scheme', route: parsed.hostname, cold_start: !tokenRef.current });
            routeTo(path);
          }
        }

        // Home-screen app shortcuts (doqit-app://shortcut/scan|search).
        // Additive alongside the paywall QR branch above - both live under
        // the same doqit-app: scheme, distinguished by hostname ('paywall'
        // vs 'shortcut'), so this never touches the campaign-token capture
        // or the QR flow's deliberate no-navigate behaviour.
        //
        // Routed through queueNavigation (not a direct navigate) so a cold
        // start that isn't authenticated yet doesn't push straight into an
        // authed-only route. The setup AddDoc's "+" button performs
        // (paywall gate, upload draft reset, reminder-default seed, entry
        // path) is intentionally NOT run here - it runs inside
        // UploadFile.tsx itself once the queued navigation actually lands
        // there (see the `shortcut=scan` query marker below), which can
        // only ever happen behind PersistLogin's auth gate. Doing it here
        // instead could fire a paywall prompt (or worse) at a logged-out
        // user before they've even reached the login screen.
        if (parsed.protocol === 'doqit-app:' && parsed.hostname === SHORTCUT_HOST) {
          const shortcutName = parsed.pathname.replace(/^\//, '');
          if (isShortcutName(shortcutName)) {
            const userProfileData = parseObjectPropertiesToCamelCase(
              userRef.current?.user || userRef.current,
            );
            const distinctId = userProfileData?.id?.toString() || '';
            const mixpanelProps: Dict = {
              $first_name: `${userProfileData?.firstName}`,
              $last_name: `${userProfileData?.lastName}`,
              $distinct_id: distinctId,
              $email: userProfileData?.email,
              shortcut_type: shortcutName,
            };
            mixpanelCustomEvent({ mixpanelProps, id: distinctId, eventName: MixpanelEventName.shortcutLaunched });

            if (shortcutName === 'search') {
              queueNavigation(linkPath.searchDocumentPath);
            } else if (shortcutName === 'add') {
              // The upload screen WITHOUT the scan query param, so it lands on
              // the four-tile chooser (file / image / photo / scan) rather than
              // firing one of them. 'scan' below deliberately keeps jumping
              // straight into the scanner, which is the point of that shortcut.
              queueNavigation(linkPath.documentUpload);
            } else {
              queueNavigation(`${linkPath.documentUpload}?${shortcutScanQueryParam}=${shortcutScanQueryValue}`);
            }
          }
          // An unrecognised shortcut name under the 'shortcut' host is a
          // silent no-op, matching the existing non-match behaviour.
        }

        // Spotlight search-result taps (doqit-app://document/<id>).
        // Additive alongside the paywall QR and shortcut branches above -
        // all live under the same doqit-app: scheme, distinguished by
        // hostname ('paywall' vs 'shortcut' vs 'document'), so this never
        // touches the campaign-token capture or the QR flow's deliberate
        // no-navigate behaviour.
        //
        // Routed through queueNavigation (not a direct navigate), for the
        // same reason as the shortcut branch above: a cold start that
        // isn't authenticated yet must not push straight into an
        // authed-only route - PersistLogin's auth gate only consumes the
        // queued path once a session is confirmed.
        if (parsed.protocol === 'doqit-app:' && parsed.hostname === DOCUMENT_HOST) {
          const documentId = parsed.pathname.replace(/^\//, '');
          if (isNumericDocumentId(documentId)) {
            trackEventAnon(MixpanelEventName.deepLinkOpened, { link_type: 'spotlight', cold_start: !tokenRef.current });
            queueNavigation(`${linkPath.editDocumentsID}/${documentId}`);
          }
          // A non-numeric (or empty) id under the 'document' host is a
          // silent no-op, matching the existing non-match behaviour for
          // other doqit-app: URLs - never navigate on unvalidated input
          // from an OS payload.
        }
      } catch {
        // Swallow malformed URLs — listener should never throw.
      }
    };

    // Cold start: capture the URL that opened the app from a killed state.
    CapApp.getLaunchUrl().then((res) => {
      if (res?.url) handleDeepLinkUrl(res.url);
    }).catch(() => { /* not available on web — fine to ignore */ });

    // Warm resume: capture URLs when the app is already running.
    let urlListener: { remove: () => void } | undefined;
    CapApp.addListener('appUrlOpen', ({ url }) => handleDeepLinkUrl(url))
      .then((sub) => { urlListener = sub; })
      .catch(() => { /* not available on web */ });

    return () => {
      urlListener?.remove();
    };
  }, []);

  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      let appStateListener: any;

      const handleAppStateChange = async ({ isActive }: { isActive: boolean }) => {
        // Our own Face ID sheet reports as resign/become active. Neither is a
        // real background trip, so neither locks nor prompts.
        if (isBiometricPromptInFlight()) {
          return;
        }
        if (!isActive) {
          // App has gone to background
          lastPausedTimeRef.current = Date.now();
          // Remember whether the cover was ALREADY up before this trip. The
          // glance-away shortcut below exists for an unlocked session that
          // was interrupted for a moment; a session that was locked on the
          // way out (a failed or cancelled Face ID, a manual lock) must come
          // back through authentication no matter how short the trip
          // (869fbb078: cover the camera, fail Face ID, swipe away and back
          // inside five seconds, and the app opened with no check at all).
          wasLockedBeforePauseRef.current = isLockedRef.current;
          sessionStorage.removeItem('biometricAuthDone');
          // Cover the screen on the way OUT, not on the way back in. Two
          // reasons, both from 869egfjpu: the iOS Face ID sheet renders over
          // whatever is already on screen, so covering only after the prompt
          // returns leaves the previous screen readable behind it; and this
          // is also the snapshot iOS puts in the app switcher.
          if (tokenRef.current) {
            dispatch(lockSession());
          }
        } else {
          // App has come to foreground

          // Check how long the app was in the background
          const timeNow = Date.now();
          const lastPausedTime = lastPausedTimeRef.current;
          const wasLockedBeforePause = wasLockedBeforePauseRef.current;
          lastPausedTimeRef.current = null;
          wasLockedBeforePauseRef.current = false;

          // Guests never lock, so there is nothing to gate.
          if (!tokenRef.current) {
            dispatch(unlockSession());
            return;
          }

          // A became-active with no matching pause is not a trip we saw go
          // out: typically the tail of our own Face ID sheet arriving after
          // the gate's grace period. It must not change the lock state in
          // either direction. Before this, a missing pause counted as a zero
          // length trip and fell into the glance-away unlock (869fbb078).
          if (lastPausedTime === null) {
            return;
          }

          const backgroundDuration = timeNow - lastPausedTime;

          // A glance away is not a session break - unlock without prompting,
          // but only for a session that was UNLOCKED when it went out. One
          // that was already behind the cover stays there and authenticates.
          // Anything not authenticated below must LEAVE the session locked,
          // never fall through to an uncovered screen.
          if (!wasLockedBeforePause && backgroundDuration < 5000) {
            dispatch(unlockSession());
            return;
          }

          // Prevent re-triggering authentication if already authenticating
          if (isAuthenticatingRef.current) {
            return;
          }

          // Set the authentication in progress flag
          isAuthenticatingRef.current = true;

          try {
            await withBiometricPrompt(() => BiometricAuth.authenticate({
              reason: 'Authenticate to continue',
              cancelTitle: 'Cancel',
              allowDeviceCredential: true,
              iosFallbackTitle: 'Use passcode',
            }));
            // Authentication succeeded
            sessionStorage.setItem('biometricAuthDone', 'true');
            dispatch(unlockSession());
          } catch {
            // Every failure path, cancel included, leaves the session LOCKED.
            // This is the whole bug: cancelling used to do nothing but remove
            // a sessionStorage key that nothing on this path ever read, so the
            // app simply stayed open (869egfjpu). NativeLockCover is now on
            // screen and carries Unlock and Sign out, so a locked session is
            // recoverable without being walk-up-and-read.
            sessionStorage.removeItem('biometricAuthDone');
          } finally {
            // Must reset on EVERY outcome. This previously reset only on a
            // non-cancel error, so after the first success or cancel the guard
            // above stayed true for the life of the launch and the app never
            // asked for Face ID again.
            isAuthenticatingRef.current = false;
          }
        }
      };

      // Setup the listener
      const setupListener = async () => {
        appStateListener = await CapApp.addListener('appStateChange', handleAppStateChange);
      };

      setupListener();

      // Clean up the event listener on unmount
      return () => {
        if (appStateListener && appStateListener.remove) {
          appStateListener.remove();
        }
      };
    }
  }, []);

  return null;
};

export default AppLifecycleHandler;
